import React, { useState, useEffect } from 'react';
import { PlayCircleIcon, ChevronLeftIcon, ChevronRightIcon, PhotoIcon, ArrowsPointingOutIcon } from '../../Icons';
import { useSwipeGesture } from '../../../hooks/useSwipeGesture';
import { PropertyFullscreenViewer } from './PropertyFullscreenViewer';

interface PropertyMediaGalleryProps {
  validImages: string[];
  mainVideo: { embedUrl: string } | null;
}

export const PropertyMediaGallery: React.FC<PropertyMediaGalleryProps> = ({ validImages, mainVideo }) => {
  const [activeIdx, setActiveIdx] = useState<number>(mainVideo ? -1 : 0);
  const [isFullscreenOpen, setIsFullscreenOpen] = useState<boolean>(false);
  const totalItems = validImages.length + (mainVideo ? 1 : 0);

  const handleNext = () => {
    if (totalItems <= 1) return;
    setActiveIdx(activeIdx === validImages.length - 1 ? (mainVideo ? -1 : 0) : activeIdx === -1 ? 0 : activeIdx + 1);
  };

  const handlePrev = () => {
    if (totalItems <= 1) return;
    setActiveIdx(activeIdx === -1 ? validImages.length - 1 : activeIdx === 0 ? (mainVideo ? -1 : validImages.length - 1) : activeIdx - 1);
  };

  const { containerProps, dragOffset, isDragging, didDrag } = useSwipeGesture({ onSwipeLeft: handleNext, onSwipeRight: handlePrev });

  useEffect(() => {
    if (isFullscreenOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') handlePrev();
      else if (e.key === 'ArrowRight') handleNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreenOpen, activeIdx, totalItems]);

  const activeNumber = activeIdx === -1 ? 1 : activeIdx + 1;
  const isVideoActive = activeIdx === -1 && mainVideo;

  return (
    <section className="space-y-2.5">
      <div 
        className={`relative w-full h-[40vh] sm:h-[46vh] md:h-[50vh] max-h-[480px] min-h-[250px] bg-gray-950 rounded-2xl overflow-hidden group shadow-lg flex items-center justify-center touch-pan-y select-none ${
          !isVideoActive && validImages[activeIdx] ? 'cursor-grab active:cursor-grabbing' : ''
        }`}
        onClick={() => {
          if (didDrag()) return;
          if (!isVideoActive && validImages[activeIdx]) setIsFullscreenOpen(true);
        }}
        {...containerProps}
      >
        {isVideoActive ? (
          <iframe className="w-full h-full" src={mainVideo.embedUrl} frameBorder="0" allowFullScreen title="Video del inmueble" />
        ) : validImages[activeIdx] ? (
          <>
            <img src={validImages[activeIdx]} className="absolute inset-0 w-full h-full object-cover blur-2xl opacity-25 scale-110 pointer-events-none select-none" aria-hidden="true" />
            <img
              src={validImages[activeIdx]}
              draggable={false}
              style={{
                transform: `translateX(${dragOffset}px)`,
                transition: isDragging ? 'none' : 'transform 0.22s ease-out',
              }}
              className="relative max-h-full max-w-full object-contain animate-fade-in transform-gpu z-[1] select-none pointer-events-none"
              decoding="async"
              alt={`Imagen ${activeNumber}`}
            />
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-500">
            <PhotoIcon className="w-16 h-16 opacity-40" />
          </div>
        )}

        {!isVideoActive && validImages[activeIdx] && (
          <button
            onClick={(e) => { e.stopPropagation(); setIsFullscreenOpen(true); }}
            className="absolute top-3 left-3 z-10 flex items-center gap-1.5 px-3 py-1 bg-black/60 hover:bg-black/80 backdrop-blur-md text-white text-[11px] font-medium rounded-full border border-white/10 shadow-sm transition-all active:scale-95"
            title="Ampliar a pantalla completa"
          >
            <ArrowsPointingOutIcon className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Ampliar</span>
          </button>
        )}

        {totalItems > 1 && (
          <>
            <button onClick={(e) => { e.stopPropagation(); handlePrev(); }} className="absolute left-3 top-1/2 -translate-y-1/2 z-10 p-2.5 rounded-full bg-black/50 hover:bg-black/80 text-white transition-all backdrop-blur-md active:scale-90" aria-label="Anterior">
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
            <button onClick={(e) => { e.stopPropagation(); handleNext(); }} className="absolute right-3 top-1/2 -translate-y-1/2 z-10 p-2.5 rounded-full bg-black/50 hover:bg-black/80 text-white transition-all backdrop-blur-md active:scale-90" aria-label="Siguiente">
              <ChevronRightIcon className="w-5 h-5" />
            </button>
          </>
        )}

        {totalItems > 0 && (
          <div className="absolute top-3 right-3 z-10 px-3 py-1 bg-black/60 backdrop-blur-md text-white text-[11px] font-bold rounded-full border border-white/10 shadow-sm">
            {isVideoActive ? `Video 1 / ${totalItems}` : `Imagen ${activeNumber} / ${validImages.length}`}
          </div>
        )}
      </div>

      {totalItems > 1 && (
        <div className="flex items-center justify-center gap-1.5 py-1 overflow-x-auto max-w-full custom-scrollbar">
          {mainVideo && (
            <button 
              onClick={() => setActiveIdx(-1)} 
              title="Ver Video"
              className={`flex items-center justify-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold transition-all ${
                activeIdx === -1 ? 'bg-sky-600 text-white scale-105 shadow' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <PlayCircleIcon className="w-3.5 h-3.5" />
              <span>Video</span>
            </button>
          )}

          {validImages.map((_, i) => (
            <button 
              key={i} 
              onClick={() => setActiveIdx(i)} 
              title={`Ir a imagen ${i + 1}`}
              className={`transition-all rounded-full ${activeIdx === i ? 'w-6 h-2 bg-sky-600 rounded-full' : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'}`}
            />
          ))}
        </div>
      )}

      <PropertyFullscreenViewer
        isOpen={isFullscreenOpen}
        onClose={() => setIsFullscreenOpen(false)}
        validImages={validImages}
        mainVideo={mainVideo}
        activeIdx={activeIdx}
        setActiveIdx={setActiveIdx}
      />
    </section>
  );
};

